import {
  Button,
  Dropdown,
  Modal,
  Pagination,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import PageTitle from '../../components/page-title';
import CustomButton from 'components/custom-button';
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { shortenName } from 'utils/short-name';

import ModalCreateAndEditVote from './components/ModalCreateAndEdit';
import { Link, useNavigate } from 'react-router-dom';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

export interface LiveShowFilter {
  limit: number;
  page: number;
  startDate?: string;
  endDate?: string;
}

const participantsPath = (id: number) => `/votes/${id}/list-user`;

/** Khớp MAX_ACTIVE_VOTES ở backend (src/vote/vote-pick.ts). */
const MAX_ACTIVE_VOTES = 5;

const VoteManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const navigate = useNavigate();
  const [filter, setFilter] = useState(initFilter as LiveShowFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleteIds, setDeleteIds] = useState<React.Key[] | null>(null);
  const [updatingVoteId, setUpdatingVoteId] = useState<number | null>(null);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    let active = true;
    const fetchVotes = async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.votes, {
          ...filter,
        });

        if (active) {
          setDataResponse({
            items: res?.data?.data || [],
            totalCount: res?.data?.metadata?.total || 0,
          });
        }
      } catch {
        if (active) notification.error({ message: 'Không tải được danh sách bình chọn.' });
      } finally {
        if (active && showLoading) setLoading(false);
      }
    };

    fetchVotes(true);
    // Trạng thái tự đổi đúng giờ dù admin đang để trang mở.
    const timer = window.setInterval(() => fetchVotes(false), 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [filter, statusUpdateData]);

  const toggleVote = async (record: any, enabled: boolean) => {
    setUpdatingVoteId(record.id);
    try {
      await AxiosUtils.patch(API.voteVisibility(record.id), { isVoteDisabled: !enabled });
      onChangeStatusUpdateData();
      notification.success({
        message: enabled ? 'Đã bật sự kiện theo lịch.' : 'Đã tạm tắt sự kiện.',
      });
    } catch {
      notification.error({ message: 'Không thay đổi được trạng thái sự kiện.' });
    } finally {
      setUpdatingVoteId(null);
    }
  };

  const phaseLabels: Record<string, { text: string; color?: string }> = {
    disabled: { text: 'Tạm tắt' },
    upcoming: { text: 'Sắp mở', color: 'processing' },
    outside_hours: { text: 'Ngoài giờ', color: 'warning' },
    open: { text: 'Đang bật', color: 'success' },
    ended: { text: 'Đã kết thúc' },
  };

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 58,
      ellipsis: true,
      key: 'id',
    },
    {
      // Cột co giãn duy nhất: nhận phần còn lại sau các cột có width.
      title: 'Tên sự kiện',
      dataIndex: 'title',
      key: 'title',
      ellipsis: { showTitle: false },
      render: (value, record) => (
        <Space size={6} style={{ maxWidth: '100%' }}>
          <Tooltip title={value}>
            <Link to={participantsPath(record.id)}>{shortenName(value || '', 40) || ''}</Link>
          </Tooltip>
          {record.isFeatured ? (
            <Tooltip title="Sự kiện mặc định cho app bản cũ, đứng đầu danh sách trên app">
              <Tag color="gold" bordered={false} style={{ marginInlineEnd: 0 }}>
                Nổi bật
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Thời gian',
      key: 'schedule',
      width: 230,
      render: (_, record) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {record.startDate ? formatedDate(record.startDate) : '—'} –{' '}
          {record.endDate ? formatedDate(record.endDate) : '—'}
          <div style={{ color: '#7a7582' }}>
            {record.startTimeVote?.slice(0, 5) || '—'} – {record.endTimeVote?.slice(0, 5) || '—'}{' '}
            mỗi ngày
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'phase',
      width: 170,
      render: (_, record) => {
        const phase = phaseLabels[record.phase] || phaseLabels.ended;
        return (
          <Space size={8} style={{ whiteSpace: 'nowrap' }}>
            <Tooltip title="Bật theo lịch; tắt để ẩn sự kiện và ngừng nhận phiếu">
              <Switch
                size="small"
                checked={!record.isVoteDisabled}
                loading={updatingVoteId === record.id}
                disabled={updatingVoteId !== null || loading}
                onChange={(checked) => toggleVote(record, checked)}
                aria-label={`${record.isVoteDisabled ? 'Bật' : 'Tạm tắt'} sự kiện ${record.title}`}
              />
            </Tooltip>
            <Tag color={phase.color}>{phase.text}</Tag>
          </Space>
        );
      },
    },

    {
      title: 'Thao tác',
      width: 150,
      key: 'action',
      fixed: 'right' as const,
      // Thêm Creator làm trong trang Danh sách, nơi nhìn thấy đủ người đã tham gia.
      render: (_, record) => (
        <Space size={0} style={{ whiteSpace: 'nowrap' }}>
          <Button
            type="link"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => navigate(participantsPath(record.id))}
          >
            Danh sách
          </Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'edit', label: 'Sửa', icon: <EditOutlined /> },
                { key: 'delete', label: 'Xóa sự kiện', icon: <DeleteOutlined />, danger: true },
              ],
              onClick: ({ key }) =>
                key === 'edit' ? handelOpenCreateEditModal(record) : setDeleteIds([record.id]),
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={`Thao tác với ${record.title}`}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData((prev) => !prev);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handleOkDeleteModal = async () => {
    if (!deleteIds?.length) return;
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteVote, { ids: deleteIds });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Đã xóa sự kiện khỏi danh sách.',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setDeleteIds(null);
    }
  };

  const handleCancelDeleteModal = () => {
    setDeleteIds(null);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  // Nhiều sự kiện chạy cùng lúc (02/10/2026), backend chặn từ sự kiện thứ
  // MAX_ACTIVE_VOTES chưa kết thúc (TOO_MANY_ACTIVE_VOTES) → báo trước.
  const openCreateModal = () => {
    const active = (dataResponse.items || []).filter((v: any) => v.phase !== 'ended');
    if (active.length < MAX_ACTIVE_VOTES) {
      handelOpenCreateEditModal(null);
      return;
    }
    Modal.info({
      title: `Đã có ${MAX_ACTIVE_VOTES} sự kiện chưa kết thúc`,
      content: `Hệ thống chạy tối đa ${MAX_ACTIVE_VOTES} sự kiện cùng lúc (tính cả sự kiện sắp diễn ra và đang tạm tắt). Hãy xoá bớt hoặc chờ một sự kiện kết thúc rồi tạo thêm.`,
      okText: 'Đã hiểu',
    });
  };

  return (
    <div>
      <PageTitle
        title="Bình chọn"
        extra={
          <CustomButton
            onClick={openCreateModal}
            bgColor="primary-green"
            text="Thêm sự kiện"
            icon={<PlusOutlined />}
          />
        }
      />
      <div className="custom-table custom-form">
        {hasSelected && (
          <Space className="mb-10">
            <Button danger onClick={() => setDeleteIds(selectedRowKeys)} loading={loading}>
              Xóa sự kiện
            </Button>
            <span>Đã chọn {selectedRowKeys.length} sự kiện</span>
          </Space>
        )}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          size="middle"
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          scroll={{ x: 760 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>

      {isOpenModalEditAndCreate && (
        <ModalCreateAndEditVote
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa sự kiện"
        open={!!deleteIds}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <p>
          Xóa {deleteIds?.length || 0} sự kiện khỏi danh sách? Sự kiện sẽ ngừng hiển thị và nhận
          phiếu; dữ liệu bình chọn cũ được giữ lại.
        </p>
      </Modal>
    </div>
  );
};

export default VoteManager;
